import {AxiosError, AxiosResponse} from 'axios';
import {ErrorResponse} from "../../models/error-response";

const fallbackMessage = 'Oops, something went wrong. Please try again later.';

async function readErrorData(data: unknown): Promise<unknown> {
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) {
    return data;
  }
  try {
    const text = typeof data.text === 'function'
      ? await data.text()
      : await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(data);
      });
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  } catch {
    return null;
  }
}

function normalizeError(status: number, data: unknown): Pick<ErrorResponse, 'message' | 'status'> {
  const fields = data && typeof data === 'object' && !Array.isArray(data)
    ? data as Record<string, unknown>
    : {};
  const backendMessage = typeof fields.message === 'string'
    ? fields.message
    : typeof data === 'string' && status < 500 && !data.trim().startsWith('<')
      ? data
      : '';
  const message = backendMessage.trim() || (
    status === 401 ? 'You are not authorized to access this resource' :
    status === 403 ? 'You do not have permission to access this resource' :
    fallbackMessage
  );
  return {...fields, message, status};
}

async function responseParser(
  response: AxiosResponse
): Promise<ErrorResponse | any> {
  if (response.status < 200 || response.status > 299) {
    throw normalizeError(response.status, await readErrorData(response.data));
  }
  return response.data;
}

async function errorParser(error: AxiosError): Promise<never> {
  if (error?.response) {
    throw normalizeError(error.response.status, await readErrorData(error.response.data));
  }
  // No HTTP response means there is no HTTP status; never expose Axios config/request.
  return Promise.reject({
    status: 0,
    message: error?.message === 'Network Error'
      ? 'Please check your connection and try again'
      : fallbackMessage,
  });
}

export {responseParser, errorParser};
